<template>
  <article>
    <h1>📌 多光谱卫星大气校正软件简介</h1>
    <section>
      <h2>一、软件登录与首页</h2>
      <div class="image-section">
        <div class="image-container">
          <img src="@/assets/documentation/denglu.png" alt="软件登录界面" class="software-image" />
          <p class="image-caption">图1：软件登录界面 - 用户身份验证与系统访问入口</p>
        </div>
        <div class="image-container">
          <img src="@/assets/documentation/shouye.png" alt="软件首页界面" class="software-image" />
          <p class="image-caption">图2：软件首页界面 - 主要功能模块导航与系统概览</p>
        </div>
      </div>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;软件采用现代化的用户界面设计，提供直观的登录验证和功能导航。登录界面确保系统安全访问，首页界面集中展示四大核心处理模块：辐射定标、大气校正、几何校正和一站式处理。
      </p>
    </section>

    <section>
      <h2>二、研究背景与目标</h2>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;随着国产高分辨率遥感卫星的大量应用，影像的定量化处理能力成为遥感应用的核心挑战。传统的大气校正方法多依赖查找表法或人工设参，存在精度不高、通用性差、操作复杂等问题。
      </p>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;为提升国产卫星地表反射率产品的精度、效率与易用性，本研究提出了一种基于辐射传输模型与卫星信息库的一站式大气校正新方法，并据此研发出国产化遥感影像预处理软件系统。
      </p>
    </section>

    <section>
      <h2>三、完整处理流程</h2>
      <div class="process-flow">
        <div class="flow-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <h4>📷 辐射定标</h4>
            <p>原始灰度值影像 → 表观辐亮度影像</p>
          </div>
        </div>
        <div class="flow-arrow">→</div>
        <div class="flow-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <h4>🌫️ 大气校正</h4>
            <p>表观辐亮度影像 → 地表反射率影像</p>
          </div>
        </div>
        <div class="flow-arrow">→</div>
        <div class="flow-step">
          <div class="step-number">3</div>
          <div class="step-content">
            <h4>🗺️ 几何校正</h4>
            <p>地表反射率影像 → 地理参考产品</p>
          </div>
        </div>
      </div>
      <p class="note">🔄 一站式处理模块将上述三个步骤整合为自动化流程，用户只需选择输入影像，系统将自动完成全部处理步骤。</p>
    </section>

    <section>
      <h2>四、核心方法与技术路径</h2>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;本软件依托先进的辐射传输模型，实现从原始影像到地表反射率产品的自动化一站式处理，具体流程如下：
      </p>
      <ol>
        <li>
          <strong>📷 辐射定标</strong>
          <p>输入：原始灰度值影像</p>
          <p>自动匹配卫星平台和传感器，提取定标系数</p>
          <p>输出：表观辐亮度影像</p>
        </li>
        <li>
          <strong>🌫️ 大气校正</strong>
          <p>通过自研辐射传输模型模拟真实大气条件，动态生成大气参数</p>
          <p>自动提取影像元数据（太阳高度、观测角、气溶胶等）</p>
          <p>输出：地表反射率影像</p>
        </li>
        <li>
          <strong>🗺️ 几何校正</strong>
          <p>读取影像RPC系数或轨道信息</p>
          <p>精准绑定地理坐标系</p>
          <p>输出：具备经纬度信息的地表反射率产品（GeoTIFF）</p>
        </li>
      </ol>
    </section>

    <section>
      <h2>五、软件功能与优势</h2>
      <ul>
        <li>
          ✅ <strong>广谱支持</strong>
          <p>适配超过 30颗国产陆地卫星（如 GF-1、GF-2、ZY-1E、JL、HJ、SDGSAT 等）多光谱影像</p>
          <p>内置卫星载荷信息库与大气参数库，无需用户手动配置</p>
        </li>
        <li>
          ✅ <strong>一站式处理流程</strong>
          <p>全自动完成 辐射定标 + 大气校正 + 几何校正</p>
          <p>批量处理多景影像，单景1G影像约5分钟即可处理完成</p>
        </li>
        <li>
          ✅ <strong>精度保障</strong>
          <p>定期基于星地同步实测数据进行验证</p>
          <p>GF-1 WFV反射率产品精度优于10%，显著优于传统方法</p>
        </li>
        <li>
          ✅ <strong>操作便捷</strong>
          <p>无需设置繁杂参数，界面化操作</p>
          <p>支持 Windows 系统独立运行，配套完整文档指导</p>
        </li>
        <li>
          ✅ <strong>开放扩展</strong>
          <p>模块化架构，便于后续集成薄云去除、BRDF校正、气溶胶反演等高级功能</p>
        </li>
      </ul>
    </section>

    <section>
      <h2>六、应用场景与价值</h2>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;该软件适用于科研院所、卫星运营单位、地方遥感应用平台等机构进行遥感影像预处理与定量分析：
      </p>
      <ul>
        <li>地表覆盖与变化检测</li>
        <li>农业监测与估产</li>
        <li>林业资源调查</li>
        <li>水质遥感与环境评估</li>
        <li>大气气溶胶反演及区域气候研究</li>
      </ul>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;其高精度、自动化、强兼容性的特点为 <strong>国产卫星遥感数据的标准化处理与共享</strong> 提供了重要支撑。
      </p>
    </section>
  </article>
</template>

<script>
export default {
  name: 'SoftwareIntro',
};
</script>

<style scoped>
/* 图片展示样式 */
.image-section {
  margin: 30px 0;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.image-container {
  text-align: center;
  background: rgba(var(--card-bg), 0.8);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(var(--primary), 0.1);
  border: 1px solid rgba(var(--primary), 0.1);
}

.software-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.software-image:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
}

.image-caption {
  margin-top: 15px;
  font-size: 14px;
  color: var(--text-secondary);
  font-style: italic;
  line-height: 1.5;
}

/* 重用 Document.vue 中的表格和列表样式 */
.main-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 25px 0;
  font-size: 15px;
  text-align: left;
  box-shadow: 0 0 15px rgba(var(--primary), 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.main-content th,
.main-content td {
  padding: 12px 15px;
  border: 1px solid rgba(var(--primary), 0.2); /* 主题色边框 */
  color: var(--text-primary);
}

.main-content thead tr {
  background-color: rgba(var(--primary), 0.2); /* 表头背景色 */
  color: var(--primary);
  text-shadow: 0 0 5px rgba(var(--primary), 0.5);
  font-weight: bold;
}

.main-content tbody tr {
  background-color: rgba(var(--card-bg), 0.6); /* 行背景色 */
}

.main-content tbody tr:nth-of-type(even) {
  background-color: rgba(var(--card-bg), 0.8); /* 隔行变色 */
}

.main-content tbody tr:hover {
  background-color: rgba(var(--primary), 0.1);
  color: var(--primary);
  text-shadow: 0 0 3px rgba(var(--primary), 0.3);
}

/* List Styles (Ordered and Unordered) */
.main-content ol,
.main-content ul {
  list-style: none;
  padding-left: 20px; /* 适当缩进 */
  margin: 20px 0;
}

.main-content ol li,
.main-content ul li {
  margin-bottom: 10px;
  color: var(--text-primary);
  line-height: 1.6;
  position: relative;
}

.main-content ol li strong,
.main-content ul li strong {
  color: var(--primary); /* 强调文本颜色 */
  text-shadow: 0 0 5px rgba(var(--primary), 0.4);
}

.main-content ol li::before {
  content: counter(list-item) ". ";
  counter-increment: list-item;
  color: var(--secondary); /* 编号颜色 */
  font-weight: bold;
  position: absolute;
  left: -25px;
  top: 0;
  width: 20px;
  text-align: right;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

.main-content ul li::before {
  content: "\2705"; /* 复选框图标 */
  color: var(--secondary); /* 图标颜色 */
  font-size: 1.1em;
  position: absolute;
  left: -50px;
  top: 0;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

.main-content ul li p {
  margin-left: 0; /* 重置列表项内段落的左外边距 */
  margin-bottom: 5px; /* 调整列表项内段落间距 */
}

.main-content p.note {
  background-color: rgba(var(--secondary), 0.1);
  border-left: 5px solid var(--secondary);
  padding: 10px 15px;
  margin-top: 20px;
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 14px;
}

/* 流程图样式 */
.process-flow {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 30px 0;
  flex-wrap: wrap;
  gap: 20px;
}

.flow-step {
  text-align: center;
  background: rgba(var(--card-bg), 0.8);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(var(--primary), 0.1);
  border: 1px solid rgba(var(--primary), 0.1);
  min-width: 200px;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  font-size: 18px;
  font-weight: bold;
  box-shadow: 0 2px 10px rgba(var(--primary), 0.3);
}

.step-content h4 {
  color: var(--primary);
  margin-bottom: 10px;
  font-size: 16px;
}

.step-content p {
  color: var(--text-secondary);
  font-size: 14px;
  margin: 0;
}

.flow-arrow {
  font-size: 24px;
  color: var(--primary);
  font-weight: bold;
  text-shadow: 0 0 10px rgba(var(--primary), 0.5);
}
</style> 